<script setup>
import SlideOver from '@/components/SlideOver.vue';
import { computed, ref } from 'vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import { useForm } from '@inertiajs/vue3';
import FormError from '@/components/Forms/FormError.vue';

const props = defineProps({
    selected: {
        type: Array,
        required: true,
    },
    subject: {
        type: Object,
        required: true,
    },
});

const tags = computed(() => {
    return props.subject.all_tags.filter((t) => props.selected.includes(t.id));
});

const open = ref(false);

const emit = defineEmits(['close']);
function dismiss() {
    open.value = false;
}

const contentBlocks = computed(() => {
    return props.subject.contentBlocks.map((cb) => {
        return {
            value: cb.id,
            text: cb.name,
        };
    });
});

const sections = computed(() => {
    return props.subject.contentBlocks
        .filter((cb) => cb.id == form.content_block_id)
        .flatMap((cb) => cb.tags)
        .flatMap((cb) => cb.sections)
        .map((cb) => {
            return {
                value: cb.name,
            };
        });
});

const form = useForm({
    content_block_id: '',
    name: null,
    section: null,
});

function submit() {
    form.transform((data) => {
        data.tag_ids = props.selected;
        return data;
    }).patch(props.subject.links.update_tags, {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
            emit('close');
            dismiss();
        },
    });
}
</script>

<template>
    <AppButton
        element="button"
        button="button"
        @click="open = true"
        desired-action
        :class="selected.length ? 'visible' : 'invisible'"
    >
        {{ $t('messages.add_to_content_block') }}
    </AppButton>
    <SlideOver size="xxl" :show="open" @close="dismiss()" :title="subject.name">
        <div class="flex flex-wrap gap-2">
            <span class="text-xs flex gap-x-2" v-for="tag in tags" :key="tag.id">
                <BadgePill>
                    {{ tag.name }}
                </BadgePill>
            </span>
        </div>

        <div>
            <FormError :value="form.errors.tag_ids" class="mt-2" />
        </div>

        <div>
            {{ $t('messages.add_tags_to') }}
        </div>

        <form action="" @submit.prevent="submit">
            <div>
                <div class="mt-1">
                    <FormLabel :value="$tc('messages.content_blocks', 1)" for="content_block_id" />
                    <FormSelect
                        id="content_block_id"
                        v-model="form.content_block_id"
                        :options="contentBlocks"
                        class="mt-1 w-full"
                        :null-option-text="$t('messages.new')"
                    />

                    <FormError :value="form.errors.content_block_id" class="mt-2" />
                </div>
            </div>
            <div v-auto-animate>
                <div class="mt-1" v-if="form.content_block_id === ''">
                    <FormLabel :value="$t('messages.name')" for="name" />
                    <FormInput
                        id="name"
                        v-model="form.name"
                        name="name"
                        type="text"
                        max="60"
                        class="mt-1 w-full"
                        :placeholder="$tc('messages.content_blocks', 1)"
                    />

                    <FormError :value="form.errors.name" class="mt-2" />
                </div>
            </div>
            <div>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.section')" for="section" />
                    <FormInput
                        id="section"
                        v-model="form.section"
                        name="section"
                        type="text"
                        maxlength="180"
                        class="mt-1 w-full"
                        list="sections"
                        :placeholder="$t('messages.(optional)')"
                    />
                    <datalist id="sections">
                        <option :value="section.value" :key="section.value" v-for="section in sections" />
                    </datalist>
                    <FormError :value="form.errors.section" class="mt-2" />
                </div>
            </div>

            <div class="flex justify-end p-2">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </SlideOver>
</template>
